import ReactJson from '@microlink/react-json-view';
import { Button, Col, Divider, Layout, List, Typography } from 'antd';
import React from 'react';
import SyntaxHighlighter from 'react-syntax-highlighter';
// import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
// import ipadImg from '../images/apipreview_3.png';

import { endpointData } from '../data/endpointdata';
import Hero from '../Components/Hero';
import TopNav from '../Components/TopNav';
import MetadataManager from '../Components/MetadataManager';

const { Content, Footer } = Layout;
const { Title } = Typography;

/**
 * PageFooter is a functional React component that renders the footer section 
 * of a web page. It displays the MenuPortal brand, contact information, and 
 * important links such as API Documentation, Terms of Service, and Privacy 
 * Policy. The component accepts a `className` prop to allow custom styling.
 * 
 * @param {Object} props - The properties object.
 * @param {string} props.className - Additional class names for custom styling.
 */

const PageFooter = (props) => (
  <Footer className={`text-white text-center text-md-left ${props.className}`} style={{ textAlign: 'center', padding: '2rem 0', backgroundColor: '#282c34' }}>
    <div className="container">
      <div className="row">
        <div className="col-md-4">
          <h5 className="font-weight-bold mb-3">MenuPortal</h5>
          <p className="mb-0 text-white">&copy;2025 MenuPortal.</p>
          <p className="mb-0 text-white">All rights reserved.</p>
        </div>
        <div className="col-md-4">
          <h5 className="font-weight-bold mb-3">Contact</h5>
          <p className="mb-0 text-white">Email: contact@mail.apix.site</p>
        </div>
        <div className="col-md-4">
          <h5 className="font-weight-bold mb-3">Links</h5>
          <p className="mb-1"><a href="/docs" className='text-white font-outfit font-weight-light letter-spacing-2'>API Documentation</a></p>
          {/* <p className="mb-1"><a href="/previewapi" className='text-white font-outfit font-weight-light letter-spacing-2'>API Preview</a></p> */}
          <p className="mb-1"><a href="/terms" className='text-white font-outfit font-weight-light letter-spacing-2'>Terms of Service</a></p>
          <p className="mb-1"><a href="/privacy" className='text-white font-outfit font-weight-light letter-spacing-2'>Privacy Policy</a></p>
        </div>
      </div>
    </div>
  </Footer>
);

const keyFeatures = [
  {
    title: 'Flexible Search & Filters',
    description: 'Built-in filtering options allow for easy search by cuisine type, location, price range, and dietary requirements.'
  },
  {
    title: 'Detailed Restaurant Information',
    description: 'Each restaurant entry includes core information such as hours of operation, contact details, and basic profile data.'
  },
  {
    title: 'Developer-Friendly Documentation',
    description: 'Our API is designed to integrate smoothly with most applications. Full documentation ensures quick setup.'
  },
  {
    title: 'Sandbox Integration',   
    description: 'Our sandbox environment allows you to test and develop your integrations without impacting production.'
  }
];

const HomePage = () => {
  return (
    <Layout className="layout overflow-hidden" >
      <MetadataManager 
        title="Home | MenuPortal"
        description="MenuPortal provides real-time restaurant data API access for developers. Get seamless integration with our JSON API."
        keywords="restaurant API, menu data, developer tools, JSON API, restaurant integration, MenuPortal"
      />
      <TopNav dark={false} transparent={true} addClass="home-navbar" />
      <Hero />
      
      {/* <hr className='style-four' /> */}
      
      <Content className='pt-5 pb-5' >
        <div className="site-layout-content font-hind">
          <div className="container">
            <Typography className='font-hind'>
              <Title level={2} style={{fontFamily:'Hind', fontSize:'2.5rem', fontWeight:700}}>
                REST API for Developers
              </Title>
              <Divider className="custom-divider" />
              <Title level={3} className='font-weight-bold font-hind text-uppercase'>Key Features:</Title>
              <List
                
                dataSource={keyFeatures}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta
                      title={<strong className='font-weight-bold font-hind text-uppercase font-size-1-25em'>{item.title}</strong>}
                      description={item.description}
                    />
                  </List.Item>
                )}
              />
            </Typography>
          </div>
        </div>
      </Content>

      <ApiExampleSection />
      <ApiPreviewSection />
      <PageFooter />
    </Layout>
  );
};

const ApiExampleSection = () => (
  <section className='bg-mid-dark pt-5 pb-5'>
    <div className="container pt-5 pb-5">
      <Typography className='font-hind text-white'>
        <Title className='text-white' level={2}>
          API Example: <span className='text-success font-hind font-weight-bold d-inline-block'>Get Single Restaurant By ID</span>
        </Title>
        <Title className='text-light' level={4}>Url:</Title>
      </Typography>
      <SyntaxHighlighter language="awk" className='syntax-highlighter-text p-3 rounded'>
        https://menuapi.apix.site/api/restaurant/4071387773960467
      </SyntaxHighlighter>
      <Divider className="custom-divider" />
      <Typography className='font-hind text-white'>
        <Title className='text-light' level={4}>Response:</Title>
      </Typography>
      <ReactJson 
        src={endpointData.restaurantById.exampleResponse} 
        iconStyle="triangle" 
        name="response" 
        collapseStringsAfterLength={20}
        style={{padding: 20, borderRadius: 5}} 
        collapsed={2} 
        className="bg-code-dark"
        theme="tomorrow" 
        enableClipboard={false} 
        displayDataTypes={false} 
      />
    </div>
  </section>
);

const ApiPreviewSection = () => (
  <section className='bg-light api-preview-section pt-5 pb-5'>
    <div className='api-preview-content'>       
      <div className='row'>   
        <Col xs={24} md={12} className='text-center text-md-left p-3'> 
          <div className='container-fluid'>
            <Typography className='font-hind text-dark pt-0 pb-3'>
              <Title level={1}>Try it out for yourself!</Title>
            </Typography>
            <Typography className='font-nunito text-dark pb-3' level={2} style={{fontSize:'1.2rem'}}>
              No credit card required. Just click the button below to try the API.
            </Typography>
             {/* <img src='./images/apipreview_3.png' alt="API Preview" className='img-fluid w-100 d-md-none' /> */}
             {/* <img src={ipadImg} alt="API Preview" className='img-fluid w-100 d-md-none' /> */}
            <Button 
              block 
              className='btn-success' 
              type="success" 
              size='large' 
              style={{ 
                marginTop: '1rem', 
                padding: '1.5rem 2rem', 
                fontFamily:'Hind',
                fontWeight: 700,  
                fontSize: '1.2rem',
                textTransform: "uppercase", 
                letterSpacing: '0.1em'
              }} 
              href="/register"
            >
              Register
            </Button>
          </div>
        </Col>
        <Col xs={24} md={12} className='text-center text-md-left p-3'>
          
        <img src="/images/apipreview_4.png" alt="API Preview" className='api-img-md d-none d-md-block' />
          
        </Col>
      </div>
      <img src="/images/apipreview_4.png" alt="API Preview" className='img-fluid api-preview-image d-block d-md-none' />
       {/* <img src={ipadImg} alt="API Preview" className='img-fluid api-preview-image d-none d-md-block' /> */}
    </div>
  </section>
);

export { PageFooter };
export default HomePage; 